/**
 * 懒加载图表组件
 * 将@ant-design/charts相关组件改为懒加载，减少首屏bundle大小
 *
 * 优势：
 * 1. 减少初始bundle - charts只在需要时加载（约883KB）
 * 2. 提升首屏加载速度
 * 3. 更好的代码分割
 *
 * 使用方式：
 * import { LazySimpleLineChart } from '@/components/common/Charts/LazyCharts';
 * <LazySimpleLineChart data={data} loading={loading} />
 */

import React, { Suspense, lazy, ComponentType } from 'react';
import { Spin } from 'antd';

// 加载状态组件
const ChartLoading: React.FC<{ height?: number }> = ({ height = 300 }) => (
  <div style={{
    height,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    <Spin size="large" tip="加载图表中..."><div style={{ minHeight: 100 }} /></Spin>
  </div>
);

// ============= Simple Charts 懒加载 =============

// 懒加载各个SimpleChart组件
export const LazySimpleLineChart = lazy(() =>
  import('./SimpleCharts').then(module => ({ default: module.SimpleLineChart }))
);

export const LazySimpleAreaChart = lazy(() =>
  import('./SimpleCharts').then(module => ({ default: module.SimpleAreaChart }))
);

export const LazySimpleColumnChart = lazy(() =>
  import('./SimpleCharts').then(module => ({ default: module.SimpleColumnChart }))
);

// Note: SimpleBarChart不存在于SimpleCharts，使用SimpleColumnChart代替
// export const LazySimpleBarChart = lazy(() =>
//   import('./SimpleCharts').then(module => ({ default: module.SimpleBarChart }))
// );

export const LazySimplePieChart = lazy(() =>
  import('./SimpleCharts').then(module => ({ default: module.SimplePieChart }))
);

export const LazySimpleGaugeChart = lazy(() =>
  import('./SimpleCharts').then(module => ({ default: module.SimpleGaugeChart }))
);

// ============= Full Charts 懒加载 =============

export const LazyLineChart = lazy(() =>
  import('./index').then(module => ({ default: module.LineChart }))
);

export const LazyAreaChart = lazy(() =>
  import('./index').then(module => ({ default: module.AreaChart }))
);

export const LazyColumnChart = lazy(() =>
  import('./index').then(module => ({ default: module.ColumnChart }))
);

export const LazyBarChart = lazy(() =>
  import('./index').then(module => ({ default: module.BarChart }))
);

export const LazyPieChart = lazy(() =>
  import('./index').then(module => ({ default: module.PieChart }))
);

export const LazyGaugeChart = lazy(() =>
  import('./index').then(module => ({ default: module.GaugeChart }))
);

export const LazyRadarChart = lazy(() =>
  import('./index').then(module => ({ default: module.RadarChart }))
);

export const LazyFunnelChart = lazy(() =>
  import('./index').then(module => ({ default: module.FunnelChart }))
);

export const LazyDualAxesChart = lazy(() =>
  import('./index').then(module => ({ default: module.DualAxesChart }))
);

export const LazyLiquidChart = lazy(() =>
  import('./index').then(module => ({ default: module.LiquidChart }))
);

export const LazyHeatmapChart = lazy(() =>
  import('./index').then(module => ({ default: module.HeatmapChart }))
);

export const LazyScatterChart = lazy(() =>
  import('./index').then(module => ({ default: module.ScatterChart }))
);

// ============= 带Suspense的包装器 =============

/**
 * 创建带Suspense的懒加载组件包装器
 */
function withSuspense<P extends object>(
  LazyComponent: ComponentType<P>,
  defaultHeight: number = 300
) {
  return (props: P & { height?: number }) => {
    const height = (props as any).height || defaultHeight;

    return (
      <Suspense fallback={<ChartLoading height={height} />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}

// ============= 导出带Suspense的组件 =============

// Simple Charts (已包含Suspense)
export const SimpleLineChartLazy = withSuspense(LazySimpleLineChart);
export const SimpleAreaChartLazy = withSuspense(LazySimpleAreaChart);
export const SimpleColumnChartLazy = withSuspense(LazySimpleColumnChart);
// Note: SimpleBarChart不存在，使用SimpleColumnChart代替
// export const SimpleBarChartLazy = withSuspense(LazySimpleBarChart);
export const SimplePieChartLazy = withSuspense(LazySimplePieChart);
export const SimpleGaugeChartLazy = withSuspense(LazySimpleGaugeChart);

// Full Charts (已包含Suspense)
export const LineChartLazy = withSuspense(LazyLineChart);
export const AreaChartLazy = withSuspense(LazyAreaChart);
export const ColumnChartLazy = withSuspense(LazyColumnChart);
export const BarChartLazy = withSuspense(LazyBarChart);
export const PieChartLazy = withSuspense(LazyPieChart);
export const GaugeChartLazy = withSuspense(LazyGaugeChart);
export const RadarChartLazy = withSuspense(LazyRadarChart);
export const FunnelChartLazy = withSuspense(LazyFunnelChart);
export const DualAxesChartLazy = withSuspense(LazyDualAxesChart);
export const LiquidChartLazy = withSuspense(LazyLiquidChart);
export const HeatmapChartLazy = withSuspense(LazyHeatmapChart);
export const ScatterChartLazy = withSuspense(LazyScatterChart);

// 默认导出（推荐使用）
export default {
  // Simple Charts (带Suspense)
  SimpleLineChart: SimpleLineChartLazy,
  SimpleAreaChart: SimpleAreaChartLazy,
  SimpleColumnChart: SimpleColumnChartLazy,
  // SimpleBarChart: SimpleBarChartLazy, // 不存在，使用SimpleColumnChart代替
  SimplePieChart: SimplePieChartLazy,
  SimpleGaugeChart: SimpleGaugeChartLazy,

  // Full Charts (带Suspense)
  LineChart: LineChartLazy,
  AreaChart: AreaChartLazy,
  ColumnChart: ColumnChartLazy,
  BarChart: BarChartLazy,
  PieChart: PieChartLazy,
  GaugeChart: GaugeChartLazy,
  RadarChart: RadarChartLazy,
  FunnelChart: FunnelChartLazy,
  DualAxesChart: DualAxesChartLazy,
  LiquidChart: LiquidChartLazy,
  HeatmapChart: HeatmapChartLazy,
  ScatterChart: ScatterChartLazy,
};
